import { faChartLine } from '@fortawesome/pro-solid-svg-icons/faChartLine';
import { faChartSimple } from '@fortawesome/pro-solid-svg-icons/faChartSimple';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faGear } from '@fortawesome/pro-solid-svg-icons/faGear';
import { faMap } from '@fortawesome/pro-solid-svg-icons/faMap';
import { faPenToSquare } from '@fortawesome/pro-solid-svg-icons/faPenToSquare';
import { faRectangleCode } from '@fortawesome/pro-solid-svg-icons/faRectangleCode';
import { faRocket } from '@fortawesome/pro-solid-svg-icons/faRocket';
import { faThumbtack } from '@fortawesome/pro-solid-svg-icons/faThumbtack';
import { BannerIcon } from '@studio/navigation';
import type { LinkOutContent, LinkOutRoute } from './types';

export const PERFORMANCE_CONTENT: LinkOutContent = {
  title: 'Performance',
  heading: 'View performance in the new App',
  message: 'Performance has moved to the new Appcues App.',
  icon: faChartLine,
};

export const FLOWS_CONTENT: LinkOutContent = {
  title: 'Flows',
  heading: 'Manage flows in the new App',
  message:
    'Flows have moved to the new Appcues App. Legacy Flows can still be created in the Appcues extension.',
  icon: faMap,
};

export const EMBEDS_CONTENT: LinkOutContent = {
  title: 'Embeds',
  heading: 'Manage embeds in the new App',
  message: 'Embeds have moved to the new Appcues App.',
  icon: faRectangleCode,
};

export const PINS_CONTENT: LinkOutContent = {
  title: 'Pins',
  heading: 'Manage pins in the new App',
  message: 'Pins have moved to the new Appcues App.',
  icon: faThumbtack,
};

export const BANNERS_CONTENT: LinkOutContent = {
  title: 'Banners',
  heading: 'Manage banners in the new App',
  message: 'Banners have moved to the new Appcues App.',
  icon: BannerIcon,
};

export const CHECKLISTS_CONTENT: LinkOutContent = {
  title: 'Checklists',
  heading: 'Manage checklists in the new App',
  message: 'Checklists have moved to the new Appcues App.',
  icon: faCheckCircle,
};

export const LAUNCHPADS_CONTENT: LinkOutContent = {
  title: 'Launchpads',
  heading: 'Manage launchpads in the new App',
  message: 'Launchpads have moved to the new Appcues App.',
  icon: faRocket,
};

export const EXPERIENCE_SETTINGS_CONTENT: LinkOutContent = {
  title: 'Settings',
  heading: 'Manage settings in the new App',
  message: 'Experience settings have moved to the new Appcues App.',
  icon: faGear,
};

export const EXPERIENCE_ANALYTICS_CONTENT: LinkOutContent = {
  title: 'Analytics',
  heading: 'View analytics in the new App',
  message: 'Experience analytics have moved to the new Appcues App.',
  icon: faChartSimple,
};

export const EXPERIENCE_BUILDER_CONTENT: LinkOutContent = {
  title: 'Builder',
  heading: 'Design in the new App',
  message: 'The experience builder has moved to the new Appcues App.',
  icon: faPenToSquare,
};

// `types` takes Agent Studio content types; JOURNEY is a legacy flow.
const listRoute = (
  list: string,
  types: string,
  content: LinkOutContent
): LinkOutRoute => ({
  pattern: new RegExp(`^/${list}/?$`, 'i'),
  resolve: () => `/experiences?types=${types}`,
  content,
  examples: [`/${list}`],
});

const routes: LinkOutRoute[] = [
  {
    pattern: /^\/performance\/?$/i,
    resolve: () => '/experiences/analytics',
    content: PERFORMANCE_CONTENT,
    examples: ['/performance'],
  },
  listRoute('flows', 'FLOW,MOBILE,MOBILE_EMBED,JOURNEY', FLOWS_CONTENT),
  listRoute('embeds', 'EMBED', EMBEDS_CONTENT),
  listRoute('pins', 'PERSISTENT', PINS_CONTENT),
  listRoute('banners', 'BANNER', BANNERS_CONTENT),
  listRoute('checklists', 'CHECKLIST', CHECKLISTS_CONTENT),
  listRoute('launchpads', 'LAUNCHPAD', LAUNCHPADS_CONTENT),
  {
    // Checklists edit their settings at `/edit` instead.
    pattern:
      /^\/(?:flows|flows-v2|mobile\/flows|embeds|pins|banners|launchpads|nps-v2)\/([^/]+)\/settings\/?$/i,
    resolve: ([, id]) => `/experiences/${id}/settings`,
    content: EXPERIENCE_SETTINGS_CONTENT,
    examples: [
      '/flows/abc/settings',
      '/flows-v2/abc/settings',
      '/mobile/flows/abc/settings',
      '/nps-v2/abc/settings',
    ],
  },
  {
    pattern: /^\/checklists\/([^/]+)\/edit\/?$/i,
    resolve: ([, id]) => `/experiences/${id}/settings`,
    content: EXPERIENCE_SETTINGS_CONTENT,
    examples: ['/checklists/abc/edit'],
  },
  {
    pattern:
      /^\/(?:flows|flows-v2|mobile\/flows|embeds|pins|banners|launchpads|checklists)\/([^/]+)\/analytics\/?$/i,
    resolve: ([, id]) => `/experiences/${id}/analytics`,
    content: EXPERIENCE_ANALYTICS_CONTENT,
    examples: [
      '/flows/abc/analytics',
      '/flows-v2/abc/analytics',
      '/mobile/flows/abc/analytics',
      '/checklists/abc/analytics',
    ],
  },
  {
    // NPS analytics has a tab segment, e.g. `/analytics/overview`.
    pattern: /^\/nps-v2\/([^/]+)\/analytics(?:\/[^/]+)?\/?$/i,
    resolve: ([, id]) => `/experiences/${id}/analytics`,
    content: EXPERIENCE_ANALYTICS_CONTENT,
    examples: ['/nps-v2/abc/analytics', '/nps-v2/abc/analytics/nps'],
  },
  {
    // `/<type>/builder` without an id creates a new experience; left alone.
    pattern: /^\/(?:flows-v2|embeds|launchpads|nps-v2)\/([^/]+)\/builder\/?$/i,
    resolve: ([, id]) => `/experiences/${id}/design`,
    content: EXPERIENCE_BUILDER_CONTENT,
    examples: [
      '/flows-v2/abc/builder',
      '/embeds/abc/builder',
      '/launchpads/abc/builder',
      '/nps-v2/abc/builder',
    ],
  },
];

export default routes;
